import type { ReadingFormat } from "@pagemates/shared";

/** What a position label needs: the book's size and how this reader counts it. */
export interface PosCtx {
  pages: number | null;
  audioSeconds?: number | null;
  myFormat?: ReadingFormat;
  /** named pages from the reading plan; a position on one wears its name */
  landmarks?: { name: string; percent: number; page?: number }[];
}

export const pageAt = (percent: number, pages: number) => Math.max(1, Math.round((percent / 100) * pages));
export const secondsAt = (percent: number, total: number) => Math.round((percent / 100) * total);

/** "4h 10m", "32 min", "9h 2m". */
export function duration(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

/** The unit part only: "p. 127", "4h 10m", or "" when there is nothing to add to the percent. */
export function unitAt(percent: number, ctx: PosCtx): string {
  const f = ctx.myFormat ?? (ctx.pages ? "pages" : "percent");
  if (f === "pages" && ctx.pages) return `p. ${pageAt(percent, ctx.pages)}`;
  if (f === "audio" && ctx.audioSeconds) return duration(secondsAt(percent, ctx.audioSeconds));
  return "";
}

/**
 * "46% · p. 127" for readers, "46% · 4h 10m" for listeners, "46%" for percent
 * readers, "Part One · 52%" on a landmark, "open" at zero. Percent alone is
 * precise to the app and vague to a human, so the unit comes along when known.
 */
export function posLabel(percent: number, ctx: PosCtx): string {
  if (percent <= 0) return "open";
  const lm = ctx.landmarks?.find((l) => l.percent === percent);
  if (lm) return `${lm.name} · ${percent}%`;
  const unit = unitAt(percent, ctx);
  return unit ? `${percent}% · ${unit}` : `${percent}%`;
}

/** Amount left to a target, in the reader's unit: "16 pages", "32 min", "12%". */
export function amountBetween(fromPercent: number, toPercent: number, ctx: PosCtx): string {
  const f = ctx.myFormat ?? (ctx.pages ? "pages" : "percent");
  const span = Math.max(0, toPercent - fromPercent);
  if (f === "pages" && ctx.pages) { const n = Math.round((span / 100) * ctx.pages); return `${n} ${n === 1 ? "page" : "pages"}`; }
  if (f === "audio" && ctx.audioSeconds) return duration((span / 100) * ctx.audioSeconds);
  return `${span}%`;
}

/** A daily pace in the reader's unit: "6 pages a day", "11 min a day", "4% a day". */
export function paceLabel(percentPerDay: number, ctx: PosCtx): string {
  const f = ctx.myFormat ?? (ctx.pages ? "pages" : "percent");
  if (f === "pages" && ctx.pages) { const n = Math.max(1, Math.ceil((percentPerDay / 100) * ctx.pages)); return `${n} ${n === 1 ? "page" : "pages"} a day`; }
  if (f === "audio" && ctx.audioSeconds) return `${duration(Math.max(60, (percentPerDay / 100) * ctx.audioSeconds))} a day`;
  return `${Math.max(1, Math.ceil(percentPerDay))}% a day`;
}
